import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Brush,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts';
import { ClockIcon, Bars3BottomLeftIcon } from '@heroicons/react/24/outline';
import { NetWorthChartTooltip } from './NetWorthChartTooltip';
import { NetWorthTable } from './NetWorthTable';
import type { NetWorthTableItem } from './NetWorthTable';
import { PeriodSelector } from './PeriodSelector';
import { GranularityToggle } from './GranularityToggle';
import { ProjectionExplanation } from './ProjectionExplanation';
import { FinancialIndependenceSection } from './FinancialIndependenceSection';
import { decimateProjectedItems } from './projectionDecimation';
import { hasValidFiExpense } from './fiExpenses';
import { ChartTableToggle, useViewPreference } from '@/components/ui/ChartTableToggle';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import {
    PrivacyAwareYAxisTick,
    PrivacyBlurFilter,
    usePrivacyBlurFilterId,
} from '@/components/charts/PrivacyAwareYAxisTick';
import { Spinner } from '@/components/ui/Spinner';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { CHART_COLORS } from '@/utils/chartColors';
import { computeChartDomain } from '@/utils/chartDomain';
import { formatDateISO } from '@/utils/date';
import type { ColumnDefinition } from '@/lib/export';
import type { NetWorthPeriodItem, ChartPeriod, PeriodAvailability, AnalyticsGranularity, NetWorthProjectionResponse } from '@/api/types';

// Map global chart colors to local names for this component
const COLORS = {
    netWorth: CHART_COLORS.netWorth.stroke,
    assets: CHART_COLORS.asset.stroke,
    debts: CHART_COLORS.debt.stroke,
    fiTarget: CHART_COLORS.pnl.stroke,
    // Investable assets line - asset indigo, read as secondary to net worth.
    investable: CHART_COLORS.asset.stroke,
};

interface BrushRange {
    startIndex: number;
    endIndex: number;
}

// Extended item type that includes the isProjected flag
interface ChartDataItem extends NetWorthPeriodItem {
    name: string;
    isProjected?: boolean;
    /** Investable-assets value for this period (FI mode only) */
    investableValue?: number | null;
}

interface NetWorthChartProps {
    /** Array of period items for chart display */
    items: (NetWorthPeriodItem & { isProjected?: boolean })[];
    /** Display currency */
    currency: string;
    /** Current period selection */
    period: ChartPeriod;
    /** Callback when period changes */
    onPeriodChange: (period: ChartPeriod) => void;
    /** Period availability flags */
    periodAvailability: PeriodAvailability;
    /** Current granularity selection */
    granularity: AnalyticsGranularity;
    /** Callback when granularity changes */
    onGranularityChange: (granularity: AnalyticsGranularity) => void;
    /** Whether user has any debt data (for conditional dual Y-axis) */
    hasDebts: boolean;
    /** Whether this is the initial data load (for loading overlay) */
    isLoading?: boolean;
    /** Whether data is being refetched (for loading overlay) */
    isFetching?: boolean;
    /** Projection data (for Future mode) */
    projectionData?: NetWorthProjectionResponse | null;
    /** Whether financial-independence mode is enabled (opt-in, default off) */
    projectionFiEnabled?: boolean;
    /** User override for return rate (null = use calculated) */
    projectionReturnRate?: number | null;
    /** User override for contribution (null = use calculated) */
    projectionContribution?: number | null;
    /** User override for yearly debt change (null = use calculated trend) */
    projectionDebtChange?: number | null;
    /** Projection years */
    projectionYears?: number;
    /** Annual expenses override for FI target (null = unset) */
    projectionAnnualExpenses?: number | null;
    /** Withdrawal rate percent driving the FI multiplier (e.g. 4.0 = 25x) */
    projectionFiWithdrawalRatePct?: number;
    /** Collection IDs counted toward FI; null = all included */
    projectionFiCollectionIds?: number[] | null;
    /** FI-specific override for the investable bucket return rate; null = auto-derived */
    projectionFiInvestableReturnRate?: number | null;
    /** Whether the post-FI withdrawal simulation is on (opt-in, default off) */
    projectionSimulateWithdrawal?: boolean;
    /** Inflation-rate override for the real-terms simulation; null = server default */
    projectionInflationRate?: number | null;
    /** Callback when the FI mode toggle changes */
    onProjectionFiEnabledChange?: (enabled: boolean) => void;
    /** Callback when return rate changes */
    onProjectionReturnRateChange?: (rate: number) => void;
    /** Callback when contribution changes */
    onProjectionContributionChange?: (amount: number) => void;
    /** Callback when yearly debt change changes */
    onProjectionDebtChangeChange?: (amount: number) => void;
    /** Callback when years changes */
    onProjectionYearsChange?: (years: number) => void;
    /** Callback when annual expenses changes */
    onProjectionAnnualExpensesChange?: (amount: number) => void;
    /** Callback when the FI withdrawal rate percent changes */
    onProjectionFiWithdrawalRatePctChange?: (pct: number) => void;
    /** Callback when the included FI collections change (null = all) */
    onProjectionFiCollectionIdsChange?: (ids: number[] | null) => void;
    /** Callback when the FI investable return rate changes */
    onProjectionFiInvestableReturnRateChange?: (rate: number) => void;
    /** Callback to reset the FI investable return rate to the derived value */
    onProjectionFiInvestableReturnRateReset?: () => void;
    /** Callback when the withdrawal-simulation toggle changes */
    onProjectionSimulateWithdrawalChange?: (enabled: boolean) => void;
    /** Callback when the inflation-rate override changes */
    onProjectionInflationRateChange?: (rate: number) => void;
    /** Callback to reset the inflation rate to the config default */
    onProjectionInflationRateReset?: () => void;
    /** Callback to reset projection to calculated values */
    onProjectionReset?: () => void;
    /** Callback to reset the whole FI section to its defaults (keeps the section open) */
    onProjectionResetFiSettings?: () => void;
    /** Callback to reset only the post-FI withdrawal simulation to its defaults */
    onProjectionResetWithdrawalSimulation?: () => void;
}

/**
 * Calculate responsive chart height based on container width.
 */
function getChartHeight(width: number): number {
    if (width < 640) return 300;
    if (width <= 1024) return 350;
    return 400;
}

/**
 * Format Y-axis values to compact notation (e.g., 10K, 1M).
 */
function formatYAxisValue(value: number, isMobile: boolean = false): string {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
        const formatted = value / 1_000_000;
        if (isMobile) {
            return formatted % 1 === 0 ? `${formatted.toFixed(0)}M` : `${formatted.toFixed(1)}M`;
        }
        return `${formatted.toFixed(1)}M`;
    }
    if (absValue >= 1_000) {
        return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toFixed(0);
}

/**
 * Get Y-axis width based on container width.
 */
function getYAxisWidth(containerWidth: number): number {
    return containerWidth < 640 ? 40 : 65;
}

/**
 * Column definitions for net worth export.
 */
const NET_WORTH_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'netWorthValue', label: 'Net Worth', format: 'money' },
    { key: 'assetsValue', label: 'Assets', format: 'money' },
    { key: 'debtsValue', label: 'Debts', format: 'money' },
];

/**
 * Net Worth chart component displaying three series: Assets, Debts, and Net Worth.
 * Features:
 * - Three-series Recharts AreaChart with distinct colors
 * - Dual Y-axis: Left for Assets & Net Worth, Right for Debts (when debts exist)
 * - Period selector for time range selection (3m, 6m, 1y, Max, Future)
 * - Granularity toggle (M/Y) for display density
 * - Chart/Table toggle for switching between visualization modes
 * - Download menu for CSV/JSON export
 * - Brush component for date range selection (only shown with 4+ data points)
 * - Dashed line styling for projected data points in Future mode
 * - ProjectionExplanation positioned above chart/table content in Future mode
 * - Responsive design
 */
export function NetWorthChart({
    items,
    currency,
    period,
    onPeriodChange,
    periodAvailability,
    granularity,
    onGranularityChange,
    hasDebts,
    isLoading = false,
    isFetching = false,
    projectionData,
    projectionFiEnabled = false,
    projectionReturnRate,
    projectionContribution,
    projectionDebtChange,
    projectionYears,
    projectionAnnualExpenses,
    projectionFiWithdrawalRatePct = 4.0,
    projectionFiCollectionIds = null,
    projectionFiInvestableReturnRate = null,
    projectionSimulateWithdrawal = false,
    projectionInflationRate = null,
    onProjectionFiEnabledChange,
    onProjectionReturnRateChange,
    onProjectionContributionChange,
    onProjectionDebtChangeChange,
    onProjectionYearsChange,
    onProjectionAnnualExpensesChange,
    onProjectionFiWithdrawalRatePctChange,
    onProjectionFiCollectionIdsChange,
    onProjectionFiInvestableReturnRateChange,
    onProjectionFiInvestableReturnRateReset,
    onProjectionSimulateWithdrawalChange,
    onProjectionInflationRateChange,
    onProjectionInflationRateReset,
    onProjectionReset,
    onProjectionResetFiSettings,
    onProjectionResetWithdrawalSimulation,
}: NetWorthChartProps) {
    const { isPrivacyModeEnabled } = usePrivacy();
    const [brushRange, setBrushRange] = useState<BrushRange | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);
    const containerRef = useRef<HTMLDivElement>(null);

    // View preference (chart or table) with localStorage persistence
    const [viewMode, setViewMode] = useViewPreference('chart-view-net-worth', 'chart');

    // Determine if we're in Future mode
    const isFutureMode = period === 'future';

    // FI mode is an opt-in view available only inside Future mode. When off,
    // the Future tab is a single total-net-worth line with no FI overlays.
    const fiActive = isFutureMode && projectionFiEnabled;

    // The investable line and FI overlays require a valid annual expense. The
    // applied value (when no override is set) backs the same fallback the FI
    // section uses, so the line shows whenever a projectable expense exists.
    const hasExpenseForFi = hasValidFiExpense(projectionAnnualExpenses, projectionData?.appliedAnnualExpenses);

    // Investable overlays are gated on both FI mode and a valid expense.
    const showInvestable = fiActive && hasExpenseForFi;

    // Compositional mode: FI on with a real (non-empty) collection subset. The
    // net worth line is then a derived sum of buckets, so the header return rate
    // is display-only (growth is driven by the investable rate) and the FI panel
    // exposes the editable investable knob.
    const isCompositional = fiActive && projectionFiCollectionIds != null && projectionFiCollectionIds.length > 0;

    // Breakdown toggle: show/hide Assets & Debts series (persisted in localStorage)
    const [showBreakdown, setShowBreakdown] = useState<boolean>(() => {
        try {
            return localStorage.getItem('chart-nw-breakdown') === 'true';
        } catch { return false; }
    });
    const toggleBreakdown = useCallback(() => {
        setShowBreakdown((prev) => {
            const next = !prev;
            try { localStorage.setItem('chart-nw-breakdown', String(next)); } catch {}
            return next;
        });
    }, []);

    // Measure container width
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // Reset brush range when period changes
    useEffect(() => {
        setBrushRange(null);
    }, [period]);

    // Reset brush range when data changes
    useEffect(() => {
        setBrushRange(null);
    }, [items]);

    const chartHeight = getChartHeight(containerWidth);
    const brushHeight = 40;
    const isMobile = containerWidth < 640;
    const yAxisWidth = getYAxisWidth(containerWidth);
    const blurFilterId = usePrivacyBlurFilterId();

    // Years that must never be dropped during decimation because callout
    // markers are anchored to them.
    const calloutYears = useMemo(() => {
        const years = new Set<number>();
        if (projectionData?.fundsDepletedYear != null) {
            years.add(projectionData.fundsDepletedYear);
        }
        if (fiActive && projectionData?.fiReachedYear != null) {
            years.add(projectionData.fiReachedYear);
        }
        return years;
    }, [fiActive, projectionData?.fundsDepletedYear, projectionData?.fiReachedYear]);

    // Investable-assets values keyed by period label, present only in FI mode.
    // Merged onto the chart data so the second line shares the same X-axis.
    const investableByLabel = useMemo(() => {
        const map = new Map<string, number>();
        if (showInvestable && projectionData?.investableItems) {
            for (const item of projectionData.investableItems) {
                map.set(item.periodLabel, item.netWorthValue);
            }
        }
        return map;
    }, [showInvestable, projectionData?.investableItems]);

    // Transform data for chart and split into historical/projected segments
    const { chartData, projectedData, tableData } = useMemo(() => {
        // Decimate long projected spans so the yearly chart stays readable on
        // mobile. Historical points are untouched; endpoints and callout years
        // are always retained.
        const visibleItems = isFutureMode
            ? decimateProjectedItems(items, calloutYears)
            : items;

        const allData: ChartDataItem[] = visibleItems.map((item) => ({
            ...item,
            name: item.periodLabel,
            investableValue: investableByLabel.get(item.periodLabel) ?? null,
        }));

        // Split data into historical and projected
        const historical: ChartDataItem[] = [];
        const projected: ChartDataItem[] = [];

        allData.forEach((item) => {
            if (item.isProjected) {
                projected.push(item);
            } else {
                historical.push(item);
            }
        });

        // If there's projected data, add the last historical point to projected
        // to ensure continuity in the line
        if (projected.length > 0 && historical.length > 0) {
            const lastHistorical = historical[historical.length - 1];
            if (lastHistorical) {
                projected.unshift({ ...lastHistorical, isProjected: false });
            }
        }

        // Table data: In Future mode, include ALL items (historical + projected) with isProjected flag.
        // Built from the undecimated source so the table never loses rows the chart skips.
        // In non-future mode, only show historical data (filter out any projected items).
        const tableItems: NetWorthTableItem[] = isFutureMode
            ? items.map(item => ({ ...item, isProjected: item.isProjected }))
            : items.filter(item => !item.isProjected);

        return {
            chartData: allData,
            projectedData: projected,
            tableData: tableItems,
        };
    }, [items, isFutureMode, calloutYears, investableByLabel]);

    // FI target reference line value (4% rule = 25x annual expenses). Computed
    // here as well as below so the left-axis domain can encompass it; the
    // domain would otherwise clip the dashed FI-target line off the top.
    const fiTargetForDomain = fiActive ? projectionData?.fiTarget ?? null : null;

    // Compute Y-axis domains:
    // - Breakdown on: proportional domain [0, max*1.05] so visual position = true proportion
    // - Breakdown off: tight domain around net worth for variation visibility
    const leftDomain = useMemo((): [number, number] | undefined => {
        if (showBreakdown) {
            const vals = chartData.flatMap((d) =>
                [d.assetsValue, d.netWorthValue, d.debtsValue].filter(Boolean)
            );
            if (vals.length === 0) return undefined;
            const max = Math.max(...vals);
            return [0, max * 1.05];
        }
        // Future/FI mode: the domain must also encompass the investable line
        // (sits below net worth) and the FI target (sits far above), or both
        // get clipped. In plain future/historical mode only net worth matters.
        const vals = chartData.map((d) => d.netWorthValue).filter(Boolean);
        if (showInvestable) {
            for (const d of chartData) {
                if (d.investableValue) vals.push(d.investableValue);
            }
        }
        if (fiTargetForDomain) {
            vals.push(fiTargetForDomain);
        }
        return computeChartDomain(vals);
    }, [chartData, showBreakdown, showInvestable, fiTargetForDomain]);

    // Show brush only with 4+ data points
    const showBrush = chartData.length >= 4;

    // Handle brush change
    const handleBrushChange = useCallback(
        (brushState: { startIndex?: number; endIndex?: number } | null) => {
            if (!brushState || brushState.startIndex === undefined || brushState.endIndex === undefined) {
                setBrushRange(null);
                return;
            }
            setBrushRange({
                startIndex: brushState.startIndex,
                endIndex: brushState.endIndex,
            });
        },
        []
    );

    // Check if there's any projected data
    const hasProjectedData = projectedData.length > 1; // More than just the bridge point

    // Check if ProjectionExplanation should be shown
    // Only show in Future mode when projection data and all required callbacks are available
    const showProjectionExplanation = isFutureMode &&
        projectionData &&
        onProjectionReturnRateChange &&
        onProjectionContributionChange &&
        onProjectionDebtChangeChange &&
        onProjectionYearsChange &&
        onProjectionReset;

    // Projection milestone callouts (only meaningful in Future mode).
    // FI target / "FI reached" are tied to the investable line and require FI
    // mode; "Funds depleted" stays tied to the total line.
    const fundsDepletedYear = isFutureMode ? projectionData?.fundsDepletedYear ?? null : null;
    const fiReachedYear = fiActive ? projectionData?.fiReachedYear ?? null : null;
    const fiTarget = fiTargetForDomain;
    const showCallouts = fundsDepletedYear != null || fiReachedYear != null;

    // Check if we have insufficient data points for chart (need 2+ for meaningful visualization)
    const hasInsufficientData = !isLoading && chartData.length <= 1;

    // Export filename
    const exportFilename = `net-worth-${formatDateISO()}`;

    return (
        <div
            ref={containerRef}
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
        >
            {/* Header with controls - mobile: 2 rows, desktop: single row */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left group: Period | Granularity (separator on desktop only) */}
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                        <PeriodSelector
                            value={period}
                            onChange={onPeriodChange}
                            availability={periodAvailability}
                            disabled={hasInsufficientData}
                        />
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <GranularityToggle
                                value={granularity}
                                onChange={onGranularityChange}
                                disableYearly={period === '3m' || period === '6m' || period === '1y'}
                                disableMonthly={period === 'future'}
                            />
                        </div>
                    </div>

                    {/* Right group: Chart/Table | Breakdown | Download */}
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                        <ChartTableToggle
                            value={viewMode}
                            onChange={setViewMode}
                            storageKey="chart-view-net-worth"
                            disabled={hasInsufficientData}
                        />
                        {hasDebts && !isFutureMode && !hasInsufficientData && (
                            <div className="mr-auto sm:mr-0 pl-3 border-l border-gray-200 dark:border-gray-600">
                                <button
                                    onClick={toggleBreakdown}
                                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
                                        showBreakdown
                                            ? 'border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                                            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark'
                                    }`}
                                    title={showBreakdown ? 'Hide assets & debts' : 'Show assets & debts'}
                                    aria-label={showBreakdown ? 'Hide assets & debts breakdown' : 'Show assets & debts breakdown'}
                                >
                                    <Bars3BottomLeftIcon className="h-5 w-5" />
                                </button>
                            </div>
                        )}
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <DownloadMenu
                                data={tableData}
                                columns={NET_WORTH_EXPORT_COLUMNS}
                                filename={exportFilename}
                                disabled={hasInsufficientData}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Projection Explanation - total-line content (text + total-line value overrides) */}
            {showProjectionExplanation && (
                <div className="mx-4 my-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
                    <ProjectionExplanation
                        projectionData={projectionData}
                        returnRate={projectionReturnRate ?? null}
                        contribution={projectionContribution ?? null}
                        debtChange={projectionDebtChange ?? null}
                        years={projectionYears ?? projectionData.projectionYears}
                        onReturnRateChange={onProjectionReturnRateChange}
                        onContributionChange={onProjectionContributionChange}
                        onDebtChangeChange={onProjectionDebtChangeChange}
                        onYearsChange={onProjectionYearsChange}
                        onReset={onProjectionReset}
                        isRefreshing={isFetching}
                        compositionalMode={isCompositional}
                        fiInvestableReturnRate={projectionFiInvestableReturnRate}
                        fiSimulateWithdrawal={projectionSimulateWithdrawal}
                        fiInflationRate={projectionInflationRate}
                    />
                </div>
            )}

            {/* Financial-independence section - toggle header below the projection text,
                expanding to FI copy, expenses/rate inputs, and the collections picker */}
            {isFutureMode && projectionData &&
                onProjectionFiEnabledChange &&
                onProjectionAnnualExpensesChange &&
                onProjectionFiWithdrawalRatePctChange &&
                onProjectionFiCollectionIdsChange && (
                <div className="mx-4 mb-3">
                    <FinancialIndependenceSection
                        projectionData={projectionData}
                        fiEnabled={projectionFiEnabled}
                        annualExpenses={projectionAnnualExpenses ?? null}
                        fiWithdrawalRatePct={projectionFiWithdrawalRatePct}
                        includedCollectionIds={projectionFiCollectionIds}
                        onFiEnabledChange={onProjectionFiEnabledChange}
                        onAnnualExpensesChange={onProjectionAnnualExpensesChange}
                        onFiWithdrawalRatePctChange={onProjectionFiWithdrawalRatePctChange}
                        onIncludedCollectionIdsChange={onProjectionFiCollectionIdsChange}
                        fiInvestableReturnRate={projectionFiInvestableReturnRate ?? null}
                        onFiInvestableReturnRateChange={onProjectionFiInvestableReturnRateChange}
                        onFiInvestableReturnRateReset={onProjectionFiInvestableReturnRateReset}
                        fiSimulateWithdrawal={projectionSimulateWithdrawal}
                        fiInflationRate={projectionInflationRate}
                        onFiSimulateWithdrawalChange={onProjectionSimulateWithdrawalChange}
                        onFiInflationRateChange={onProjectionInflationRateChange}
                        onFiInflationRateReset={onProjectionInflationRateReset}
                        onResetFiSettings={onProjectionResetFiSettings}
                        onResetWithdrawalSimulation={onProjectionResetWithdrawalSimulation}
                        isRefreshing={isFetching}
                    />
                </div>
            )}

            {/* Projection milestone callouts - drawdown depletion and FI reached */}
            {isFutureMode && projectionData && showCallouts && (
                <div className="mx-4 mb-3 flex flex-col gap-2 sm:flex-row">
                    {fundsDepletedYear != null && (
                        <p
                            role="status"
                            className="flex-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                        >
                            Funds depleted in {fundsDepletedYear}
                        </p>
                    )}
                    {fiReachedYear != null && (
                        <p
                            role="status"
                            className="flex-1 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300"
                        >
                            FI first reached in {fiReachedYear}
                        </p>
                    )}
                </div>
            )}

            {/* Content area - conditionally render chart, table, or insufficient data state */}
            {hasInsufficientData ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <ClockIcon className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                        Not enough data yet
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Record values monthly (end of month works best) to track trends.
                        Charts appear after 2+ months of data.
                    </p>
                    <Link
                        to="/assets"
                        className="mt-6 inline-flex items-center px-4 py-2 text-accent hover:text-accent/80 transition-colors"
                    >
                        Go to Assets →
                    </Link>
                </div>
            ) : viewMode === 'table' ? (
                <div className="relative">
                    {/* Loading overlay for table view */}
                    {(isLoading || isFetching) && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                    {isLoading ? 'Loading...' : 'Updating...'}
                                </span>
                            </div>
                        </div>
                    )}
                    <NetWorthTable items={tableData} currency={currency} />
                </div>
            ) : (
                <div
                    className="relative w-full chart-container px-4"
                    style={{ height: chartHeight + (showBrush ? brushHeight + 20 : 0) }}
                >
                    {/* Loading overlay */}
                    {(isLoading || isFetching) && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 flex items-center justify-center z-10">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                    {isLoading ? 'Loading...' : isFutureMode ? 'Predicting...' : 'Updating...'}
                                </span>
                            </div>
                        </div>
                    )}
                    <style>{`
                        .chart-container .recharts-cartesian-grid-horizontal line,
                        .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #e5e7eb;
                        }
                        .dark .chart-container .recharts-cartesian-grid-horizontal line,
                        .dark .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #374151;
                        }
                        .chart-container .recharts-brush-slide {
                            fill: ${COLORS.netWorth};
                            fill-opacity: 0.2;
                        }
                        .dark .chart-container .recharts-brush-slide {
                            fill: ${COLORS.netWorth};
                            fill-opacity: 0.3;
                        }
                        .chart-container .recharts-brush-traveller rect {
                            fill: ${COLORS.netWorth};
                        }
                    `}</style>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <PrivacyBlurFilter id={blurFilterId} />
                                {/* Assets gradient */}
                                <linearGradient id="assetsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={CHART_COLORS.asset.fill}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={CHART_COLORS.asset.fill}
                                        stopOpacity={0.05}
                                    />
                                </linearGradient>
                                {/* Debts gradient */}
                                <linearGradient id="debtsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={CHART_COLORS.debt.fill}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={CHART_COLORS.debt.fill}
                                        stopOpacity={0.05}
                                    />
                                </linearGradient>
                                {/* Investable assets gradient (FI mode secondary line) */}
                                <linearGradient id="investableGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={CHART_COLORS.asset.fill}
                                        stopOpacity={0.15}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={CHART_COLORS.asset.fill}
                                        stopOpacity={0.02}
                                    />
                                </linearGradient>
                                {/* Net Worth gradient */}
                                <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop
                                        offset="5%"
                                        stopColor={CHART_COLORS.netWorth.fill}
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={CHART_COLORS.netWorth.fill}
                                        stopOpacity={0.05}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                                className="text-gray-600 dark:text-gray-400"
                            />
                            {/* Left Y-axis: Assets & Net Worth (or just Net Worth in future mode) */}
                            <YAxis
                                key={`left-${isPrivacyModeEnabled}`}
                                yAxisId="left"
                                domain={leftDomain}
                                tick={
                                    <PrivacyAwareYAxisTick
                                        formatValue={(value) => formatYAxisValue(value, isMobile)}
                                        blurred={isPrivacyModeEnabled}
                                        blurFilterId={blurFilterId}
                                        fontSize={isMobile ? 10 : 12}
                                    />
                                }
                                tickLine={false}
                                axisLine={false}
                                width={yAxisWidth}
                            />
                            <Tooltip
                                content={<NetWorthChartTooltip currency={currency} isFutureMode={isFutureMode} showInvestable={showInvestable} />}
                                cursor={{ stroke: COLORS.netWorth, strokeWidth: 1, strokeDasharray: '3 3' }}
                            />

                            {/* FI target (4% rule = 25x annual expenses) - only in Future mode when set */}
                            {isFutureMode && fiTarget ? (
                                <ReferenceLine
                                    yAxisId="left"
                                    y={fiTarget}
                                    stroke={COLORS.fiTarget}
                                    strokeDasharray="6 4"
                                    strokeWidth={1.5}
                                    label={{
                                        value: 'FI target',
                                        position: 'insideTopRight',
                                        fill: COLORS.fiTarget,
                                        fontSize: 11,
                                    }}
                                />
                            ) : null}

                            {/* Assets area - only render when breakdown is active */}
                            {!isFutureMode && showBreakdown && (
                                <Area
                                    type="monotone"
                                    dataKey="assetsValue"
                                    name="assetsValue"
                                    stroke={COLORS.assets}
                                    strokeWidth={2}
                                    fill="url(#assetsGradient)"
                                    animationDuration={300}
                                    animationEasing="ease-out"
                                    yAxisId="left"
                                />
                            )}

                            {/* Debts area - only render when breakdown is active and user has debts */}
                            {!isFutureMode && hasDebts && showBreakdown && (
                                <Area
                                    type="monotone"
                                    dataKey="debtsValue"
                                    name="debtsValue"
                                    stroke={COLORS.debts}
                                    strokeWidth={2}
                                    fill="url(#debtsGradient)"
                                    animationDuration={300}
                                    animationEasing="ease-out"
                                    yAxisId="left"
                                />
                            )}

                            {/* Net Worth area - solid line for all data (or historical portion) */}
                            {(!isFutureMode || !hasProjectedData) && (
                                <Area
                                    type="monotone"
                                    dataKey="netWorthValue"
                                    name="netWorthValue"
                                    stroke={COLORS.netWorth}
                                    strokeWidth={3}
                                    fill="url(#netWorthGradient)"
                                    animationDuration={300}
                                    animationEasing="ease-out"
                                    yAxisId="left"
                                />
                            )}

                            {/* In Future mode with projected data, render two separate areas */}
                            {isFutureMode && hasProjectedData && (
                                <>
                                    {/* Historical data - solid line */}
                                    <Area
                                        type="monotone"
                                        dataKey={(dataPoint: ChartDataItem) => {
                                            if (!dataPoint.isProjected) {
                                                return dataPoint.netWorthValue;
                                            }
                                            return null;
                                        }}
                                        name="netWorthHistorical"
                                        stroke={COLORS.netWorth}
                                        strokeWidth={3}
                                        fill="url(#netWorthGradient)"
                                        animationDuration={300}
                                        animationEasing="ease-out"
                                        connectNulls={false}
                                        yAxisId="left"
                                    />
                                    {/* Projected data - dashed line */}
                                    <Area
                                        type="monotone"
                                        dataKey={(dataPoint: ChartDataItem) => {
                                            // Include last historical point for continuity
                                            const dataIndex = chartData.findIndex(d => d.periodLabel === dataPoint.periodLabel);
                                            const nextPoint = dataIndex < chartData.length - 1 ? chartData[dataIndex + 1] : null;

                                            if (dataPoint.isProjected) {
                                                return dataPoint.netWorthValue;
                                            }
                                            // Include point if next point is projected (bridge point)
                                            if (nextPoint?.isProjected) {
                                                return dataPoint.netWorthValue;
                                            }
                                            return null;
                                        }}
                                        name="netWorthProjected"
                                        stroke={COLORS.netWorth}
                                        strokeWidth={3}
                                        strokeDasharray="5 5"
                                        fill="url(#netWorthGradient)"
                                        animationDuration={300}
                                        animationEasing="ease-out"
                                        connectNulls={true}
                                        yAxisId="left"
                                    />
                                </>
                            )}

                            {/* Investable assets - secondary dashed line spanning the full
                                range; reads below the primary net-worth line. */}
                            {showInvestable && investableByLabel.size > 0 && (
                                <Area
                                    type="monotone"
                                    dataKey="investableValue"
                                    name="investableValue"
                                    stroke={COLORS.investable}
                                    strokeWidth={2}
                                    strokeDasharray="4 3"
                                    strokeOpacity={0.9}
                                    fill="url(#investableGradient)"
                                    animationDuration={300}
                                    animationEasing="ease-out"
                                    connectNulls={true}
                                    yAxisId="left"
                                />
                            )}

                            {showBrush && (
                                <Brush
                                    dataKey="name"
                                    height={brushHeight}
                                    stroke={COLORS.netWorth}
                                    fill="transparent"
                                    onChange={handleBrushChange}
                                    startIndex={brushRange?.startIndex}
                                    endIndex={brushRange?.endIndex}
                                    travellerWidth={10}
                                >
                                    <AreaChart data={chartData}>
                                        <Area
                                            type="monotone"
                                            dataKey="netWorthValue"
                                            stroke={COLORS.netWorth}
                                            fill={COLORS.netWorth}
                                            fillOpacity={0.2}
                                        />
                                    </AreaChart>
                                </Brush>
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}
